import Order from "@domain/Interfaces/Order.d";
import { Box, TableContainer, Table, TableBody } from "@mui/material";
import TotalTablePagination from "@presentation/components/TotalTablePagination";
import React, { useEffect, useState } from "react";
import {
  calculateNumberOfEmptyRows,
  defaultTableSx,
  getComparator,
  getPageNumber,
  stableSort,
} from "@presentation/components/table/utils";
import EmptyRows from "@presentation/components/table/EmptyRow";
import { Maintenance } from "@domain/entities/Maintenance";
import MaintenanceHead from "@presentation/components/maintenance/MaintenanceHead";
import MaintenanceRow from "@presentation/components/maintenance/MaintenanceRow";
import { MaintenanceHeadCell } from "@domain/Interfaces/HeadCell.d";
import { MaintenanceHeadCellData } from "@domain/Interfaces/HeadCellData";
import useStoredPagination from "@presentation/utils/StoredPagination";
import useStoredRowsPerPage from "@presentation/utils/StoredRowsPerPage";

type MaintenanceTableProps = {
  headCells: MaintenanceHeadCell[];
  rows: Maintenance[];
};

const MaintenanceTable = ({ headCells, rows }: MaintenanceTableProps): React.JSX.Element => {
  const [storedPagination, setStoredPagination] = useStoredPagination("maintenance", {
    page: 0,
    order: "asc",
    orderBy: "startDate",
  });
  const [rowsPerPage, setRowsPerPage] = useStoredRowsPerPage(10);
  const [pageNumber, setPageNumber] = useState<number>(getPageNumber(rowsPerPage, rows.length));
  if (storedPagination.page > pageNumber) {
    setStoredPagination({ ...storedPagination, page: 0 });
  }

  const handleRequestSort = (_event: React.MouseEvent<unknown>, property: keyof MaintenanceHeadCellData): void => {
    const isAsc = storedPagination.orderBy === property && storedPagination.order === "asc";
    setStoredPagination({ ...storedPagination, order: isAsc ? "desc" : "asc", orderBy: property });
  };

  const handleChangePage = (_event: unknown, newPage: number): void => {
    setStoredPagination({ ...storedPagination, page: newPage - 1 });
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setRowsPerPage(parseInt(event.target.value));
    setStoredPagination({ ...storedPagination, page: 0 });
  };

  useEffect(() => {
    setPageNumber(getPageNumber(rowsPerPage, rows.length));
  }, [rowsPerPage, rows]);

  const emptyRows = calculateNumberOfEmptyRows(storedPagination.page, rowsPerPage, rows.length);

  return (
    <Box sx={{ width: "100%" }}>
      <TableContainer>
        <Table sx={defaultTableSx} aria-labelledby="tableTitle">
          <MaintenanceHead
            order={storedPagination.order as Order}
            orderBy={storedPagination.orderBy as keyof MaintenanceHeadCellData}
            onRequestSort={handleRequestSort}
            headCells={headCells}
          />
          <TableBody>
            {stableSort<Maintenance>(
              rows,
              getComparator(storedPagination.order as Order, storedPagination.orderBy as keyof MaintenanceHeadCellData)
            )
              .slice(storedPagination.page * rowsPerPage, storedPagination.page * rowsPerPage + rowsPerPage)
              .map((row) => {
                return <MaintenanceRow key={`maintenance-list-${row.id}`} row={row} />;
              })}
            <EmptyRows emptyNumber={emptyRows} />
          </TableBody>
        </Table>
      </TableContainer>
      <TotalTablePagination
        page={storedPagination.page}
        pageNumber={pageNumber}
        rowsPerPage={rowsPerPage}
        nbItems={rows.length}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Box>
  );
};

export default MaintenanceTable;
